/**
 * Public answer-rating page (fr-docs E5-R05): `/avaliar#<token>`, alias `/rate#<token>`.
 *
 * Reached from the stars at the bottom of a Smith answer e-mail. No sign-in: the signed link
 * is the credential, and the BFF relays only the token and the form. States: loading →
 * invalid (one shape for every bad link) | form → submitted, and the form stays editable
 * while the link is valid (a resubmission updates the rating).
 *
 * The token rides in the URL FRAGMENT, which a browser never sends, so neither Traefik's nor
 * nginx's access log records it. Links sent before that carry it in the path
 * (`/avaliar/<token>`): they still work, and once read the address bar is rewritten to the
 * fragment form, so a reload or a bookmark does not send it again.
 *
 * The star the reader clicked arrives as `?pt=` / `?as=` and is pre-selected; nothing is
 * sent until they press the button. The page opens in the answer's language unless the
 * reader already chose one, and that pick is not persisted. The form itself is `RatingPanel`,
 * which the run page also renders beside the sandboxed reply preview.
 */
import { Icon, Notice, Spinner } from '@evt/flow-stream-ui'
import { isFeedbackTokenShape } from '@portal/contracts'
import { useEffect, useRef } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { isInvalidFeedbackLink } from '../api/feedback'
import { RatingPanel } from '../components/RatingPanel'
import { readRatingToken, tokenFromLocation } from '../components/rating-link'
import { useFeedbackContext } from '../hooks/use-feedback-context'
import { describeApiError } from '../i18n/errors'
import { useI18n } from '../i18n/I18nProvider'
import { LanguageSwitcher } from '../i18n/LanguageSwitcher'
import { storedLocale } from '../i18n/locale'
import { pageLocaleChange } from './rate/form'

export function RatePage() {
  const params = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [query] = useSearchParams()
  const { t, locale, setLocale, formatDate } = useI18n()
  const token = tokenFromLocation(params.token, location.hash)
  const context = useFeedbackContext(token)
  const localePicked = useRef(false)

  // A link already sent carries the token in the path; keep it only in the fragment from here on.
  useEffect(() => {
    if (!params.token) return
    const route = location.pathname.split('/')[1] ?? 'avaliar'
    navigate({ pathname: `/${route}`, search: location.search, hash: `#${params.token}` }, { replace: true })
  }, [params.token, location.pathname, location.search, navigate])

  // Open in the answer's language, once, unless the reader already chose one.
  useEffect(() => {
    if (!context.data || localePicked.current) return
    localePicked.current = true
    const change = pageLocaleChange(context.data.locale, storedLocale(), locale)
    if (change) setLocale(change.locale, change.options)
  }, [context.data, locale, setLocale])

  const invalid = !isFeedbackTokenShape(token) || (context.isError && isInvalidFeedbackLink(context.error))
  const expiresAt = isFeedbackTokenShape(token) ? readRatingToken(token)?.expiresAt : undefined
  const expired = expiresAt !== undefined && expiresAt.getTime() <= Date.now()

  return (
    <div className="portal-center">
      <div className="login-card card rate-card">
        <div className="login-head">
          <div className="login-logo">
            <Icon name="bolt" size={22} />
          </div>
          <h1>{t.feedback.pageTitle}</h1>
          <p className="muted">{t.feedback.subtitle}</p>
        </div>

        {invalid ? (
          <Notice kind="danger" className="login-notice">
            <strong>{t.feedback.invalidTitle}</strong>{' '}
            {expired && expiresAt ? t.feedback.expiredOn(formatDate(expiresAt)) : t.feedback.invalidBody}
          </Notice>
        ) : context.isPending ? (
          <div className="rate-loading" aria-live="polite">
            <Spinner size={18} /> {t.common.loading}
          </div>
        ) : context.isError ? (
          <div className="login-body">
            <Notice kind="danger" className="login-notice">
              {describeApiError(t, context.error)}
            </Notice>
            <button type="button" className="btn btn-block" onClick={() => void context.refetch()}>
              {t.common.retry}
            </button>
          </div>
        ) : (
          <>
            <div className="rate-context">
              <div className="rate-context-label muted">{t.feedback.ratingFor}</div>
              <div className="rate-context-flow">{context.data.flowName}</div>
              {context.data.reference && <div className="rate-context-ref">{context.data.reference}</div>}
              {context.data.answeredAt && (
                <div className="muted rate-context-date">
                  {t.feedback.answeredAt(formatDate(context.data.answeredAt))}
                </div>
              )}
            </div>
            <RatingPanel token={token} context={context.data} query={query} />
          </>
        )}

        <div className="login-lang">
          <LanguageSwitcher />
        </div>
      </div>
    </div>
  )
}
